/**
 * Events (/events, administrators and co-administrators) per the reference: New event; each upcoming event with
 * its counts, spot rule, actions and — for a raffle — where it stands; Raffle History (every draw: date, event,
 * which draw, places, the pool, winners, the fingerprint, who ran it — the draw is random, this is the evidence that
 * is kept); how spots are given; the RSVP page (preview); past events.
 * An outing made on the Volunteers page (1.1.0) is here too, with its capacity. 1.5.1: New starts with the type (an
 * event or a workshop); Event & activity support has its own section; the tech's workshop proposals show here while
 * one is waiting. 1.5.8: Past events is always there (collapsed, newest first) — each event's date, place, how many
 * were invited, said yes and came, and its RSVP list: the record that is kept, also for an event that came in from a
 * roster workbook. 1.7.5: "Events in charts" under the list (attendance by event, the year as a line) and Print in
 * the head; Raffle History stays the wide table it is.
 *
 * 1.8.2 (D-1.8.2-2, D-1.8.2-4): the page opens on its calendar. The head has exactly New, Print and the Month view
 * (List · Grid) — New opens one sheet (An event · An announcement · A survey, ./features/events/NewSheet182), Print one
 * small sheet (This month · All upcoming events, the language, Print · Copy · Email — ./features/events/PrintSheet182).
 * The month lives in the address (`?m=yyyyMM`, `?v=list` for the List), with Previous, Today and Next. Grid: the month
 * as a table (./features/events/EventsGrid182), a tap on a day opens that day's events under it as the 1.7.9 rows;
 * List: the 1.7.9 month list. The Surveys and Announcements sections follow as lists, without New buttons of their own;
 * the 1.7.7 month tools (Copy last month, Announce the month, Request volunteers) stay on /events/month, linked from
 * under the calendar. Then the sections the page always had.
 *
 * 1.8.4 (D-1.8.4-6): Events, trimmed. Each event once — the month (List or Grid) is where events are listed, as short
 * cards that show only what applies (./features/events/ShortCards184: name · day and time · place · kind · the numbers
 * that apply · Open, and Check-in on the day of an event with RSVP); the "Upcoming events" stack, the two explainer
 * cards (to the staff guide and the Ask helper), "Events in charts" (into the 12-month report) and Event & activity
 * support (one line while activities made before exist; new ones start from Book → Volunteer help) left the page.
 * What is left: New · Print · List/Grid → the month → Surveys and Announcements → Past events (collapsed, with the
 * reports and Raffle History — ./features/events/Reports184). A workshop proposal waiting for staff stays at the top.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { serviceDateKey } from '@shared/dates';
import { addMonths, isMonth, monthOf } from '@shared/event-calendar';
import { NEW_RIGHTS, newChoicesFor, type NewThing } from '@shared/new-kinds';
import { ACCESS, type Role } from '@shared/roles';
import { inLang } from '@shared/surveys';
import { useMe } from '@/auth/useMe';
import { useAnnounce } from '@/components/Announcer';
import { Icon } from '@/components/Icons';
import { PageHead } from '@/components/Later';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Sheet } from '@/components/ui/sheet';
import { Updated } from '@/components/Updated';
import { formatDayKey, formatMonthYear } from '@/lib/dates';
import { useAnnouncements } from '@/features/announcements/api';
import { useEvents } from '@/features/events/api';
import { useCalendarRefresh } from '@/features/events/api177';
import { EventsGrid182 } from '@/features/events/EventsGrid182';
import { eventsByDay } from '@/features/events/month/days';
import { MonthSentLines } from '@/features/events/month/MonthShare';
import { NewSheet182 } from '@/features/events/NewSheet182';
import { PrintSheet182 } from '@/features/events/PrintSheet182';
import { PastEvents184 } from '@/features/events/Reports184';
import { ShortCards184 } from '@/features/events/ShortCards184';
import { SurveyStateChip } from '@/features/surveys/SurveyStateChip';
import { useSurveys } from '@/features/surveys/api';
import { ActivitiesSection, ActivitiesToday } from '@/features/activities/ActivitiesSection';
import { useActivities } from '@/features/activities/api';
import { WorkshopProposals } from '@/features/activities/Workshops';
import '@/styles/num175.css';
import '@/styles/ev182.css';
import '@/styles/ev184.css';

type View = 'grid' | 'list';

export function EventsPage({ role: roleProp, rights = NEW_RIGHTS }: { role?: Role | null; rights?: Readonly<Record<NewThing, readonly Role[]>> } = {}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'es' ? 'es' : 'en';
  const announce = useAnnounce();
  const me = useMe();
  // the page is given the role by the app; on its own it asks who is signed in
  const role: Role | null = roleProp !== undefined ? roleProp : (me.data?.role ?? null);
  const list = useEvents();
  const refresh = useCalendarRefresh();
  const data = list.data;
  const todayKey = data?.todayKey ?? serviceDateKey();
  const [params, setParams] = useSearchParams();
  const asked = params.get('m') ?? '';
  const month = isMonth(asked) ? asked : monthOf(todayKey);
  // 25 Oct 2026 (D-1025-EVENTS): the page opens as a list of what is coming up; the month is one link away (`?v=grid` still opens the old grid for a saved link)
  const view: View = params.get('v') === 'grid' ? 'grid' : 'list';
  const monthLabel = formatMonthYear(`${month}01`, lang);
  const choices = newChoicesFor(role, rights);
  const canMakeEvents = choices.includes('event');
  const canPrint = !!role && (ACCESS.staff as readonly Role[]).includes(role);
  const canWrite = role === 'admin' || role === 'coadmin';

  const [newOn, setNewOn] = useState<{ dayKey: string | null } | null>(null);
  const [printOpen, setPrintOpen] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const [focus, setFocus] = useState<{ day: string | null; tick: number }>({ day: null, tick: 0 });
  const [said, setSaid] = useState<{ text: string; bad: boolean } | null>(null);
  const opener = useRef<HTMLElement | null>(null);

  const all = useMemo(() => [...(data?.upcoming ?? []), ...(data?.past ?? [])], [data]);
  const byDay = useMemo(() => eventsByDay(all, month), [all, month]);
  const upcoming = useMemo(() => [...(data?.upcoming ?? [])].sort((a, b) => a.dayKey.localeCompare(b.dayKey) || a.name.localeCompare(b.name)), [data]);
  const monthEvents = useMemo(() => [...byDay.values()].flat(), [byDay]);
  const dayEvents = selected ? (byDay.get(selected) ?? []) : [];

  const say = (text: string, bad = false) => {
    setSaid({ text, bad });
    announce(text, bad ? 'assertive' : 'polite');
  };
  const setMonthView = (m: string, v: View = view) => {
    const next = new URLSearchParams(params);
    next.set('m', m);
    if (v === 'grid') next.set('v', 'grid');
    else next.delete('v');
    setParams(next);
  };

  // a new month on screen: the heading is said aloud; a day of another month is no longer open
  const shown = useRef(month);
  useEffect(() => {
    if (shown.current === month) return;
    shown.current = month;
    setSelected((s) => (s && s.startsWith(month) ? s : null));
    announce(t('ev182.cal.caption', { month: monthLabel }));
  }, [month, monthLabel, announce, t]);

  const pickDay = (dayKey: string) => {
    setSelected(dayKey);
    setFocus((f) => ({ day: dayKey, tick: f.tick }));
    const n = byDay.get(dayKey)?.length ?? 0;
    announce(`${t('ev182.cal.dayBtn', { day: formatDayKey(dayKey, lang), count: n })}.`);
  };
  const moveTo = (dayKey: string) => {
    if (!dayKey.startsWith(month)) setMonthView(monthOf(dayKey));
    setFocus((f) => ({ day: dayKey, tick: f.tick + 1 }));
  };
  const openNew = (dayKey: string | null, from: HTMLElement) => {
    opener.current = from;
    setNewOn({ dayKey });
  };

  return (
    <div className="page events-page" data-testid="events-page" data-month={month}>
      <PageHead title={t('events.title')} eyebrow={t('events.eyebrow')} id="h-events">
        <div className="chips ev182-head" role="group" aria-label={t('ev182.head')} data-testid="events-head-actions">
          {choices.length ? (
            <Button onClick={(e) => openNew(null, e.currentTarget)} aria-label={t('ev182.newName')} data-testid="open-new-event">
              <Icon name="plus" /> {t('ev182.new')}
            </Button>
          ) : null}
          {canPrint ? (
            <Button
              variant="secondary"
              onClick={(e) => {
                opener.current = e.currentTarget;
                setPrintOpen(true);
              }}
              data-testid="events-print"
            >
              <Icon name="printer" /> {t('ev182.print')}
            </Button>
          ) : null}
          {canPrint ? (
            <Link className="btn secondary ev1025-bymonth" to={`/events/month?m=${month}`} data-testid="events-by-month">
              <Icon name="calendarEmpty" /> {t('ev1025.list.byMonth')}
            </Link>
          ) : null}
        </div>
      </PageHead>

      {/* a workshop proposal waiting for staff stays at the top while one waits (it is nothing when none does) */}
      <WorkshopProposals />

      <section className="ev182-cal" aria-labelledby="events-month-h" data-testid="events-calendar">
        <div className="ev182-calhead">
          <h2 id="events-month-h" data-testid="events-month-h">
            {view === 'grid' ? monthLabel : t('ev1025.list.title')}
          </h2>
          {view === 'grid' ? (
          <nav className="chips ev182-nav no-print" aria-label={t('ev182.cal.nav')}>
            <Button variant="secondary" size="sm" onClick={() => setMonthView(addMonths(month, -1))} data-testid="events-prev">
              {t('ev182.cal.prev')}
            </Button>
            <Button variant="secondary" size="sm" onClick={() => setMonthView(monthOf(todayKey))} aria-current={month === monthOf(todayKey) ? 'date' : undefined} data-testid="events-today">
              {t('ev182.cal.today')}
            </Button>
            <Button variant="secondary" size="sm" onClick={() => setMonthView(addMonths(month, 1))} data-testid="events-next">
              {t('ev182.cal.next')}
            </Button>
          </nav>
          ) : null}
        </div>
        {said ? (
          <p className={said.bad ? 'field-err' : 'cal177m-line'} role={said.bad ? 'alert' : undefined} data-testid="events-said">
            <Icon name={said.bad ? 'alert' : 'check'} width={16} height={16} aria-hidden="true" />
            <span>{said.text}</span>
          </p>
        ) : null}
        {list.isLoading ? <p role="status">{t('app.loading')}</p> : null}
        {list.isError ? <p className="field-err">{t('events.error')}</p> : null}
        {data && view === 'grid' && !monthEvents.length ? (
          <p className="help calm cal177m-line" data-testid="events-month-none">
            <Icon name="calendarEmpty" aria-hidden="true" />
            <span>{t('ev182.cal.none', { month: monthLabel })}</span>
          </p>
        ) : null}
        {data && view === 'grid' ? (
          <>
            <Card as="div" className="ev182-card">
              <CardContent>
                <EventsGrid182 month={month} monthLabel={monthLabel} events={monthEvents} todayKey={todayKey} selected={selected} focusDay={focus.day} focusTick={focus.tick} onPick={pickDay} onMove={moveTo} onAdd={canMakeEvents ? (dayKey, from) => openNew(dayKey, from) : null} />
              </CardContent>
            </Card>
            {selected ? (
              <section className="ev182-day" aria-labelledby="events-day-h" data-testid="events-day">
                <div className="ev182-dayhead">
                  <h3 id="events-day-h">{formatDayKey(selected, lang)}</h3>
                  {canMakeEvents && selected >= todayKey ? (
                    <Button variant="secondary" size="sm" onClick={(e) => openNew(selected, e.currentTarget)} data-testid="events-day-new">
                      <Icon name="plus" /> {t('ev182.cal.dayNew')}
                    </Button>
                  ) : null}
                </div>
                {dayEvents.length ? (
                  <ShortCards184 what={formatDayKey(selected, lang)} events={dayEvents} todayKey={todayKey} canWrite={canWrite} level={4} />
                ) : (
                  <p className="help calm" data-testid="events-day-none">
                    {t('ev182.cal.dayNone')}
                  </p>
                )}
              </section>
            ) : null}
          </>
        ) : null}
        {/* List: the month's events as short cards — only what applies, and Open (1.8.4) */}
        {data && view === 'list' && upcoming.length ? <ShortCards184 what={t('ev1025.list.title')} events={upcoming} todayKey={todayKey} canWrite={canWrite} /> : null}
        {data && view === 'list' && !upcoming.length ? (
          <p className="help calm cal177m-line" data-testid="events-upcoming-none">
            <Icon name="calendarEmpty" aria-hidden="true" />
            <span>{t('ev1025.list.none')}</span>
          </p>
        ) : null}
        <MonthSentLines month={month} enabled={canPrint} />
        {canWrite && view === 'grid' ? (
          <p className="help ev182-tools no-print">
            <Link to={`/events/month?m=${month}`} data-testid="events-month-link">
              {t('ev182.cal.monthTools')}
            </Link>
          </p>
        ) : null}
      </section>

      <div className="ev182-lists">
        <SurveysSection lang={lang} />
        <AnnouncementsSection lang={lang} />
      </div>

      {/* 20 Oct 2026 (D-1020-SMALL): today's confirmed activities moved here from the Housekeeping Day */}
      {canWrite ? <ActivitiesToday /> : null}
      {canWrite ? <SupportLine todayKey={todayKey} /> : null}
      {data ? <PastEvents184 past={data.past} all={all} todayKey={todayKey} history={data.history} canPdf={canPrint} /> : null}
      {data ? <Updated at={list.dataUpdatedAt} fetching={list.isFetching} /> : null}

      <Sheet open={!!newOn} onOpenChange={(o) => (!o ? setNewOn(null) : undefined)}>
        {newOn ? (
          <NewSheet182
            role={role}
            rights={rights}
            todayKey={todayKey}
            returnFocusTo={opener}
            onClose={() => setNewOn(null)}
            onCreated={() => {
              // the "+" of that day may be gone: focus goes back to the page's heading instead
              if (!opener.current?.isConnected) opener.current = document.getElementById('h-events');
              refresh();
            }}
            {...(newOn.dayKey ? { startDayKey: newOn.dayKey, start: 'event' as const } : month > monthOf(todayKey) ? { startDayKey: `${month}01` } : {})}
          />
        ) : null}
      </Sheet>
      <Sheet open={printOpen} onOpenChange={(o) => (!o ? setPrintOpen(false) : undefined)}>
        {printOpen ? <PrintSheet182 month={month} monthLabel={monthLabel} events={all} todayKey={todayKey} returnFocusTo={opener} onClose={() => setPrintOpen(false)} say={say} /> : null}
      </Sheet>
    </div>
  );
}

/** 1.8.2: the surveys as a list (the newest first, as the server gives them), each leading to its page; no New of its own. */
function SurveysSection({ lang }: { lang: 'en' | 'es' }) {
  const { t } = useTranslation();
  const q = useSurveys();
  const rows = q.data?.surveys;
  return (
    <Card as="section" aria-labelledby="h-ev-surveys" data-testid="events-surveys">
      <CardHeader>
        <CardTitle id="h-ev-surveys">{t('ev182.surveys.title')}</CardTitle>
        <Link to="/events/surveys" data-testid="events-surveys-link">
          {t('ev182.surveys.all')}
        </Link>
      </CardHeader>
      <CardContent>
        {q.isLoading ? <p role="status">{t('app.loading')}</p> : null}
        {rows && !rows.length ? (
          <p className="help calm" data-testid="events-surveys-none">
            {t('ev182.surveys.none')}
          </p>
        ) : null}
        {rows?.length ? (
          <ul className="plain-list ev182-list" aria-label={t('ev182.surveys.label')}>
            {rows.slice(0, 6).map((s) => (
              <li key={s.slug}>
                <Link to={`/events/surveys/${encodeURIComponent(s.slug)}`} data-testid={`events-survey-${s.slug}`}>
                  {inLang(s.title, lang)}
                </Link>
                <SurveyStateChip state={s.state} />
              </li>
            ))}
          </ul>
        ) : null}
      </CardContent>
    </Card>
  );
}

/** 1.8.2: the announcements as a list, each leading to its page (a draft opens there in its sheet); no New of its own. */
function AnnouncementsSection({ lang }: { lang: 'en' | 'es' }) {
  const { t } = useTranslation();
  const q = useAnnouncements();
  const rows = q.data?.announcements;
  return (
    <Card as="section" aria-labelledby="h-ev-ann" data-testid="events-announcements">
      <CardHeader>
        <CardTitle id="h-ev-ann">{t('ev182.ann.title')}</CardTitle>
        <Link to="/events/announcements" data-testid="events-announcements-link">
          {t('ev182.ann.all')}
        </Link>
      </CardHeader>
      <CardContent>
        {q.isLoading ? <p role="status">{t('app.loading')}</p> : null}
        {rows && !rows.length ? (
          <p className="help calm" data-testid="events-ann-none">
            {t('ev182.ann.none')}
          </p>
        ) : null}
        {rows?.length ? (
          <ul className="plain-list ev182-list" aria-label={t('ev182.ann.label')}>
            {rows.slice(0, 6).map((a) => {
              const subject = (lang === 'es' && a.subject.es ? a.subject.es : a.subject.en) || a.subject.es || t('ann176.noSubject');
              const draft = a.status === 'draft';
              return (
                <li key={a.id}>
                  <Link to={draft ? `/events/announcements?draft=${encodeURIComponent(a.id)}` : `/events/announcements/${encodeURIComponent(a.id)}`} data-testid={`events-ann-${a.id}`}>
                    {subject}
                  </Link>
                  <span className="help">{t(`ann176.status.${a.status}`)}</span>
                </li>
              );
            })}
          </ul>
        ) : null}
      </CardContent>
    </Card>
  );
}

/**
 * 1.8.4 — Event & activity support left the page (1.8.3 moved its start to Book → Volunteer help, where the activities
 * are listed). The activities made before keep their presses (Send, Confirm, Record hours, the standby list) here, behind
 * one line that shows only while any exist; Book → Volunteer help links to it (`/events#h-activities` opens it).
 */
function SupportLine({ todayKey }: { todayKey: string }) {
  const { t } = useTranslation();
  const q = useActivities();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const count = q.data?.activities.length ?? 0;
  const asked = location.hash === '#h-activities';
  useEffect(() => {
    if (asked && count) setOpen(true);
  }, [asked, count]);
  useEffect(() => {
    if (open && asked) document.getElementById('h-activities')?.scrollIntoView?.({ block: 'start' });
  }, [open, asked]);
  if (!count) return null;
  return (
    <Card as="section" className="ev184-support" aria-label={t('ev184.support.summary', { count })} data-testid="events-support">
      <details open={open} onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}>
        <summary data-testid="events-support-summary">{t('ev184.support.summary', { count })}</summary>
        <div>
          <p className="help">{t('ev184.support.lead')}</p>
          {open ? <ActivitiesSection todayKey={todayKey} /> : null}
        </div>
      </details>
    </Card>
  );
}
